import React, { useState } from 'react';
import { AuthUser } from '../types';
import { api } from '../api';
import { tg } from '../telegram';
import {
  User,
  ShieldCheck,
  UserCheck,
  KeyRound,
  Lock,
  Eye,
  EyeOff,
  Save,
  LogOut,
  Shield,
  Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';

interface Props {
  currentUser: AuthUser;
  onBack: () => void;
  onUserUpdated: (user: AuthUser) => void;
  onLogout: () => void;
}

export const MyPageScreen: React.FC<Props> = ({
  currentUser,
  onUserUpdated,
  onLogout,
}) => {
  // Profile update state
  const [displayName, setDisplayName] = useState(currentUser.name);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Password update state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [isChangingPass, setIsChangingPass] = useState(false);

  const isAdmin = currentUser.role === 'ADMIN';

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = displayName.trim();
    if (!trimmed || trimmed.length < 2) {
      toast.error('Tên hiển thị phải có ít nhất 2 ký tự');
      return;
    }
    if (trimmed === currentUser.name) {
      toast('Tên hiển thị không có thay đổi');
      return;
    }

    setIsUpdatingProfile(true);
    tg.haptic.impact('medium');

    try {
      const res = await api.updateProfile({ name: trimmed });
      if (res.success && res.user) {
        onUserUpdated(res.user);
        toast.success('Đã cập nhật tên hiển thị thành công!');
        tg.haptic.success();
      } else {
        toast.error(res.error || 'Cập nhật tên thất bại');
        tg.haptic.error();
      }
    } catch (err: any) {
      toast.error(err.message || 'Lỗi khi cập nhật tên');
      tg.haptic.error();
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error('Vui lòng nhập mật khẩu hiện tại');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      toast.error('Mật khẩu mới phải có tối thiểu 6 ký tự');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Xác nhận mật khẩu mới không trùng khớp');
      return;
    }
    if (currentPassword === newPassword) {
      toast.error('Mật khẩu mới không được trùng mật khẩu cũ');
      return;
    }

    setIsChangingPass(true);
    tg.haptic.impact('medium');

    try {
      const res = await api.changePassword({ currentPassword, newPassword });
      if (res.success) {
        toast.success(res.message || 'Đổi mật khẩu thành công!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        tg.haptic.success();
      } else {
        toast.error(res.error || 'Đổi mật khẩu thất bại');
        tg.haptic.error();
      }
    } catch (err: any) {
      toast.error(err.message || 'Lỗi khi đổi mật khẩu');
      tg.haptic.error();
    } finally {
      setIsChangingPass(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Profile Overview Card */}
      <div
        className="card"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          background: 'linear-gradient(135deg, var(--bg-surface) 0%, var(--bg-surface-subtle) 100%)',
          border: '1.5px solid var(--border-color)',
          padding: '18px 16px',
        }}
      >
        <div
          style={{
            width: 54,
            height: 54,
            borderRadius: '50%',
            background: isAdmin
              ? 'linear-gradient(135deg, #D97706 0%, #92400E 100%)'
              : 'linear-gradient(135deg, #059669 0%, #064E3B 100%)',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: isAdmin
              ? '0 4px 12px rgba(217, 119, 6, 0.25)'
              : '0 4px 12px rgba(5, 150, 105, 0.25)',
          }}
        >
          {isAdmin ? <ShieldCheck size={28} /> : <UserCheck size={28} />}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: 'var(--text-primary)',
              letterSpacing: '-0.01em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {currentUser.name}
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
              marginTop: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span>Tài khoản:</span>
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                backgroundColor: 'var(--bg-surface-subtle)',
                padding: '2px 6px',
                borderRadius: 4,
                fontWeight: 700,
                color: 'var(--text-primary)',
                fontSize: 12,
              }}
            >
              @{currentUser.username}
            </code>
          </div>
          <div style={{ marginTop: 6 }}>
            <span
              className={`badge ${isAdmin ? 'badge-warning' : 'badge-success'}`}
              style={{ fontSize: 11, padding: '3px 8px', fontWeight: 700 }}
            >
              {isAdmin ? 'Quản Lý / Chủ Quán (ADMIN)' : 'Nhân Viên Ca / Barista (STAFF)'}
            </span>
          </div>
        </div>
      </div>

      {/* Role Permissions Callout */}
      <div
        style={{
          padding: '12px 14px',
          borderRadius: 'var(--radius-md)',
          backgroundColor: 'var(--bg-surface-subtle)',
          border: '1px solid var(--border-color)',
          borderLeft: `4px solid ${isAdmin ? 'var(--primary-accent)' : 'var(--color-success)'}`,
          fontSize: 12.5,
          lineHeight: 1.5,
          color: 'var(--text-secondary)',
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
        }}
      >
        <Shield size={18} color={isAdmin ? 'var(--primary-accent)' : 'var(--color-success)'} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          {isAdmin ? (
            <span>
              <strong style={{ color: 'var(--text-primary)' }}>Phạm vi quyền ADMIN:</strong> Toàn quyền nhập kho tổng (L1), chuyển kho (L2), chốt ca (L3), báo hủy (L4), cân bằng kho (L5) và quản trị Master Data hệ thống.
            </span>
          ) : (
            <span>
              <strong style={{ color: 'var(--text-primary)' }}>Phạm vi quyền STAFF:</strong> Vận hành trực tiếp quầy bar (L2 Chuyển nhận kho, L3 Chốt ca đối soát BOM, L4 Báo hủy hỏng, L5 Cân bằng tồn quầy). Khóa quyền Nhập hàng kho tổng L1.
            </span>
          )}
        </div>
      </div>

      {/* Card 1: Change Display Name */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <User size={18} color="var(--primary-accent)" />
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
            1. Đổi Tên Hiển Thị
          </h3>
        </div>

        <form onSubmit={handleUpdateProfile} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                marginBottom: 6,
                display: 'block',
              }}
            >
              Tên hiển thị trên hệ thống & phiếu chốt ca:
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Nhập tên hiển thị mới (VD: Anh Nam, Chị Linh)..."
              className="input-field"
              disabled={isUpdatingProfile}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isUpdatingProfile || !displayName.trim() || displayName.trim() === currentUser.name}
            style={{ width: '100%', minHeight: 46 }}
          >
            {isUpdatingProfile ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Đang Lưu Thay Đổi...
              </>
            ) : (
              <>
                <Save size={16} /> Lưu Tên Hiển Thị
              </>
            )}
          </button>
        </form>
      </div>

      {/* Card 2: Change Password */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <KeyRound size={18} color="var(--primary-accent)" />
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
            2. Đổi Mật Khẩu Đăng Nhập
          </h3>
        </div>

        <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Current Password */}
          <div>
            <label
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                marginBottom: 6,
                display: 'block',
              }}
            >
              Mật khẩu hiện tại:
            </label>
            <div className="password-input-wrapper">
              <input
                type={showCurrentPass ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Nhập mật khẩu đang dùng..."
                className="input-field"
                style={{ paddingRight: 48 }}
                disabled={isChangingPass}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowCurrentPass(!showCurrentPass)}
                aria-label={showCurrentPass ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
              >
                {showCurrentPass ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                marginBottom: 6,
                display: 'block',
              }}
            >
              Mật khẩu mới (Tối thiểu 6 ký tự):
            </label>
            <div className="password-input-wrapper">
              <input
                type={showNewPass ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nhập mật khẩu mới..."
                className="input-field"
                style={{ paddingRight: 48 }}
                disabled={isChangingPass}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowNewPass(!showNewPass)}
                aria-label={showNewPass ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
              >
                {showNewPass ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                marginBottom: 6,
                display: 'block',
              }}
            >
              Xác nhận lại mật khẩu mới:
            </label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPass ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Nhập lại chính xác mật khẩu mới..."
                className="input-field"
                style={{ paddingRight: 48 }}
                disabled={isChangingPass}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowConfirmPass(!showConfirmPass)}
                aria-label={showConfirmPass ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
              >
                {showConfirmPass ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isChangingPass || !currentPassword || !newPassword || !confirmPassword}
            style={{ width: '100%', minHeight: 46, marginTop: 2 }}
          >
            {isChangingPass ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Đang Cập Nhật Mật Khẩu...
              </>
            ) : (
              <>
                <Lock size={16} /> Cập Nhật Mật Khẩu Mới
              </>
            )}
          </button>
        </form>
      </div>

      {/* Card 3: Logout Action */}
      <div
        className="card"
        style={{
          border: '1.5px solid #FEE2E2',
          backgroundColor: '#FFF8F8',
          padding: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#991B1B' }}>Đăng Xuất Thiết Bị</div>
            <div style={{ fontSize: 12, color: '#7F1D1D', marginTop: 2 }}>
              Xóa phiên làm việc hiện tại trên trình duyệt này
            </div>
          </div>

          <button
            onClick={onLogout}
            className="btn btn-danger"
            style={{
              padding: '9px 14px',
              fontSize: 13,
              minHeight: 40,
              flexShrink: 0,
            }}
          >
            <LogOut size={15} /> Đăng Xuất
          </button>
        </div>
      </div>
    </div>
  );
};
